import React, { createContext, useState, useContext, useEffect, useRef } from 'react';

const CartContext = createContext();

const GUEST_CART_KEY = 'modische_guest_cart';
const GUEST_WISHLIST_KEY = 'modische_guest_wishlist';

export const CartProvider = ({ children }) => {
  // Initialize state directly from localStorage if available
  const [cart, setCart] = useState(() => {
    try {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [wishlist, setWishlist] = useState(() => {
    try {
      const stored = localStorage.getItem(GUEST_WISHLIST_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const isInitialHydrated = useRef(false);

  // Safety token fallback check across multiple storage keys
  const getActiveToken = () => {
    return (
      localStorage.getItem('modische_token') ||
      localStorage.getItem('modische_auth_token') ||
      localStorage.getItem('token')
    );
  };

  const [hasToken, setHasToken] = useState(!!getActiveToken());

  // 1. HYDRATE & MERGE ON APP LOAD OR USER LOGIN
  useEffect(() => {
    let isMounted = true;

    const syncFromDatabaseCloud = async () => {
      const token = getActiveToken();

      // If guest user, retain local storage contents
      if (!token) {
        try {
          const localCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
          const localWishlist = JSON.parse(localStorage.getItem(GUEST_WISHLIST_KEY) || '[]');
          if (isMounted) {
            setCart(localCart);
            setWishlist(localWishlist);
            isInitialHydrated.current = true;
          }
        } catch {
          if (isMounted) isInitialHydrated.current = true;
        }
        return;
      }

      // Logged-in user: Fetch profile and merge local guest items with cloud database
      try {
        const response = await fetch('https://api.modischekledings.com/api/users/profile', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const userData = await response.json();

        if (response.ok && userData && isMounted) {
          const cloudCart = (userData.cart || [])
            .map((item) => {
              if (item.productId && typeof item.productId === 'object') {
                return {
                  ...item.productId,
                  _id: item.productId._id,
                  size: item.size,
                  quantity: item.quantity
                };
              }
              return null;
            })
            .filter(Boolean);

          const cloudWishlist = userData.wishlist || [];

          // Merge local guest cart into cloud cart
          const localCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]');
          const mergedCart = [...cloudCart];

          localCart.forEach((guestItem) => {
            const guestId = guestItem._id || guestItem.id;
            const existingIndex = mergedCart.findIndex(
              (cItem) => (cItem._id === guestId || cItem.id === guestId) && cItem.size === guestItem.size
            );
            if (existingIndex > -1) {
              mergedCart[existingIndex].quantity += guestItem.quantity || 1;
            } else {
              mergedCart.push(guestItem);
            }
          });

          // Merge local guest wishlist into cloud wishlist
          const localWishlist = JSON.parse(localStorage.getItem(GUEST_WISHLIST_KEY) || '[]');
          const mergedWishlist = [...cloudWishlist];

          localWishlist.forEach((guestWishItem) => {
            const guestId = guestWishItem._id || guestWishItem.id;
            const exists = mergedWishlist.some(
              (wItem) => (wItem._id === guestId || wItem.id === guestId)
            );
            if (!exists) {
              mergedWishlist.push(guestWishItem);
            }
          });

          setCart(mergedCart);
          setWishlist(mergedWishlist);

          // Update local cache
          localStorage.setItem(GUEST_CART_KEY, JSON.stringify(mergedCart));
          localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(mergedWishlist));
        }
      } catch (err) {
        console.error('Failed fetching persistent profile rows from cloud:', err);
      } finally {
        if (isMounted) {
          isInitialHydrated.current = true;
        }
      }
    };

    syncFromDatabaseCloud();

    return () => {
      isMounted = false;
    };
  }, [hasToken]);

  // 2. PERSIST TO LOCALSTORAGE AND PUSH BULK CHANGES TO BACKEND
  useEffect(() => {
    if (!isInitialHydrated.current) return;

    // Always keep localStorage updated for instantaneous retrieval & guest survival
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
      localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(wishlist));
    } catch (e) {
      console.warn('LocalStorage save failed:', e);
    }

    const token = getActiveToken();
    if (!token) return;

    // Sync to cloud if authenticated
    const syncToDatabaseCloud = async () => {
      try {
        await fetch('https://api.modischekledings.com/api/users/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ cart, wishlist })
        });
      } catch (err) {
        console.error('Cloud database synchronization failed:', err);
      }
    };

    const scheduleSync = setTimeout(() => {
      syncToDatabaseCloud();
    }, 400);

    return () => clearTimeout(scheduleSync);
  }, [cart, wishlist]);

  // 🛍️ CART ACTIONS (Accessible to both Guests and Logged-in Users)
  const addToCart = (product, selectedSize) => {
    if (!selectedSize) {
      alert('Please select a size before adding to bag.');
      return false;
    }

    setCart((prevCart) => {
      const targetId = product._id || product.id;
      const existingIndex = prevCart.findIndex(
        (item) => (item._id === targetId || item.id === targetId) && item.size === selectedSize
      );

      if (existingIndex > -1) {
        return prevCart.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
        );
      } else {
        return [...prevCart, { ...product, _id: targetId, size: selectedSize, quantity: 1 }];
      }
    });
    return true;
  };

  const decreaseQuantity = (productId, selectedSize) => {
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex(
        (item) => (item._id === productId || item.id === productId) && item.size === selectedSize
      );

      if (existingIndex > -1) {
        if (prevCart[existingIndex].quantity > 1) {
          return prevCart.map((item, idx) =>
            idx === existingIndex ? { ...item, quantity: item.quantity - 1 } : item
          );
        } else {
          return prevCart.filter((_, idx) => idx !== existingIndex);
        }
      }
      return prevCart;
    });
  };

  const removeFromCart = (productId, size) => {
    setCart((prevCart) =>
      prevCart.filter((item) => !((item._id === productId || item.id === productId) && item.size === size))
    );
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(GUEST_CART_KEY);
  };

  // ❤️ WISHLIST ACTIONS (Accessible to both Guests and Logged-in Users)
  const toggleWishlist = (product) => {
    setWishlist((prevWishlist) => {
      const targetId = product._id || product.id;
      const exists = prevWishlist.find((item) => item._id === targetId || item.id === targetId);
      if (exists) {
        return prevWishlist.filter((item) => !(item._id === targetId || item.id === targetId));
      } else {
        return [...prevWishlist, { ...product, _id: targetId }];
      }
    });
    return true;
  };

  const getCartCount = () => cart.reduce((total, item) => total + (item.quantity || 1), 0);

  const triggerAuthSync = () => setHasToken(!!getActiveToken());

  return (
    <CartContext.Provider
      value={{
        cart,
        wishlist,
        addToCart,
        decreaseQuantity,
        removeFromCart,
        clearCart,
        toggleWishlist,
        getCartCount,
        triggerAuthSync,
        setCart,
        setWishlist
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);